import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheckIcon, 
  AcademicCapIcon, 
  UserGroupIcon, 
  RocketLaunchIcon,
  StarIcon,
  CheckCircleIcon,
  LightBulbIcon,
  GlobeAltIcon,
  CodeBracketIcon,
  ChartBarIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const About = () => {
  const stats = [
    { number: "500+", label: "Digital Assets", icon: CodeBracketIcon },
    { number: "50+", label: "Expert Courses", icon: AcademicCapIcon },
    { number: "10K+", label: "Happy Learners", icon: UserGroupIcon },
    { number: "24/7", label: "Support", icon: ShieldCheckIcon }
  ];

  const values = [
    {
      icon: LightBulbIcon,
      title: "Innovation",
      description: "Constantly pushing boundaries to deliver cutting-edge digital solutions and educational content."
    },
    {
      icon: ShieldCheckIcon,
      title: "Quality",
      description: "Every product and course is meticulously crafted to meet the highest standards of excellence."
    },
    {
      icon: HeartIcon,
      title: "Excellence",
      description: "Committed to delivering the highest quality digital assets and educational content."
    },
    {
      icon: GlobeAltIcon,
      title: "Accessibility",
      description: "Making quality education and digital tools accessible to everyone, everywhere."
    }
  ];

  const team = [
    {
      name: "Digital Experts",
      role: "Technology Specialists",
      description: "Industry professionals with years of experience in digital solutions and technology innovation."
    },
    {
      name: "Software Engineers",
      role: "Development Team",
      description: "Skilled developers creating automation tools and practical solutions for real-world problems."
    },
    {
      name: "Content Creators",
      role: "Educational Team",
      description: "Dedicated educators crafting comprehensive courses and learning materials."
    },
    {
      name: "Support Team",
      role: "Customer Success",
      description: "Committed professionals ensuring your learning journey is smooth and successful."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden futuristic-panel">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-grid-pattern"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 opacity-70"></div>
        <div className="container relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-effect mb-6">
              <StarIcon className="h-5 w-5 text-primary" />
              <span className="text-sm">About Pingfence</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Empowering <span className="text-gradient">Digital Excellence</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Pingfence is India's largest digital asset library, offering high-quality templates, tools, 
              and professional development resources. We bridge the gap between ideas and execution, 
              providing actionable knowledge and tools for the modern digital landscape.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 btn btn-primary text-lg px-8 py-3"
              >
                Explore Products
                <RocketLaunchIcon className="h-5 w-5" />
              </Link>
              <Link
                to="/digital-bundles"
                className="inline-flex items-center gap-2 btn btn-secondary text-lg px-8 py-3"
              >
                Browse Courses
                <AcademicCapIcon className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-secondary">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">{stat.number}</div>
                <div className="text-gray-800">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Our <span className="text-gradient">Mission</span>
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                To democratize access to high-quality cybersecurity education and digital tools, 
                empowering individuals and organizations to navigate the complex digital landscape 
                with confidence and expertise.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <span>Provide practical, hands-on learning experiences</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <span>Bridge the gap between academic theory and real-world application</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                  <span>Provide comprehensive support and resources for continuous learning</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-2 bg-primary/20 rounded-3xl blur-xl"></div>
              <div className="relative bg-secondary p-8 rounded-2xl border border-primary/20">
                <h3 className="text-2xl font-bold mb-4 text-primary">Our Vision</h3>
                <p className="text-gray-300 leading-relaxed">
                  To become India's leading platform for digital asset distribution and education, 
                  recognized for our commitment to quality, innovation, and excellence. We envision 
                  a world where everyone has access to the knowledge and tools needed to thrive in the 
                  digital age.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-secondary">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-gradient">Core Values</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-2xl mx-auto">
              These principles guide everything we do, from content creation to service delivery
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-secondary border border-primary/10 hover:border-primary/30 transition-colors">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                  <value.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-gray-800">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              Meet Our <span className="text-gradient">Team</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-2xl mx-auto">
              A diverse group of experts passionate about digital innovation, education, and technology
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-secondary border border-primary/10 hover:border-primary/30 transition-colors">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                  <UserGroupIcon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                <p className="text-primary font-medium mb-3">{member.role}</p>
                <p className="text-gray-800 text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-20 bg-secondary">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              What We <span className="text-gradient">Offer</span>
            </h2>
            <p className="text-lg text-gray-800 max-w-2xl mx-auto">
              Comprehensive solutions for your digital asset and learning needs
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 rounded-xl bg-secondary border border-primary/10">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mb-4">
                <CodeBracketIcon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Digital Assets</h3>
              <p className="text-gray-800 mb-4">
                High-quality scripts, tools, and resources for digital professionals and enthusiasts.
              </p>
              <Link to="/products" className="text-primary hover:text-primary/80 font-medium">
                Explore Assets →
              </Link>
            </div>
            <div className="p-6 rounded-xl bg-secondary border border-primary/10">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mb-4">
                <AcademicCapIcon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Expert Courses</h3>
              <p className="text-gray-800 mb-4">
                Comprehensive cybersecurity courses designed by industry professionals with hands-on labs.
              </p>
              <Link to="/digital-bundles" className="text-primary hover:text-primary/80 font-medium">
                Browse Courses →
              </Link>
            </div>
            <div className="p-6 rounded-xl bg-secondary border border-primary/10">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mb-4">
                <ChartBarIcon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Professional Development</h3>
              <p className="text-gray-800 mb-4">
                Career-focused resources and guidance to help you advance in the cybersecurity field.
              </p>
              <Link to="/products" className="text-primary hover:text-primary/80 font-medium">
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">
              Ready to <span className="text-gradient">Get Started?</span>
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Join thousands of learners and professionals who trust Pingfence for their cybersecurity 
              education and digital asset needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 btn btn-primary text-lg px-8 py-3"
              >
                Explore Products
                <RocketLaunchIcon className="h-5 w-5" />
              </Link>
              <Link
                to="/digital-bundles"
                className="inline-flex items-center gap-2 btn btn-secondary text-lg px-8 py-3"
              >
                Start Learning
                <AcademicCapIcon className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 